import { useAlerts } from "components/Alerts/AlertProvider";
import { FacilityPickerFragment } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { FileRejection } from "react-dropzone";

import { useBillOfLadingDiscard } from "./useBillOfLadingDiscard";
import { useBillOfLadingSubmit } from "./useBillOfLadingSubmit";

/**
 * Upload state and the upload → extract handoff for
 * `BillOfLadingUploadDialog`, pulled into its own hook so the dialog's render
 * function stays under the complexity lint's line-count limit. The submit and
 * discard mechanics are further split into `useBillOfLadingSubmit` and
 * `useBillOfLadingDiscard` for the same reason.
 */
export function useBillOfLadingUpload({
  tenantId,
  initialFacilityId,
  onClose,
  onExtractionReady,
}: {
  tenantId: string;
  initialFacilityId?: string;
  onClose: () => void;
  onExtractionReady?: (documentIds: string[], facilityId: string) => void;
}) {
  const [facility, setFacility] = useState<FacilityPickerFragment | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dropRejectedFiles, setDropRejectedFiles] = useState<FileRejection[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(true);

  const alerts = useAlerts();
  const discard = useBillOfLadingDiscard({ onDiscarded: onClose });
  const { submit } = useBillOfLadingSubmit({
    tenantId,
    onClose,
    onExtractionReady,
    onUploaded: discard.setUploadedDocumentId,
  });

  const facilityId = facility?.id ?? initialFacilityId;
  const canSubmit = !!file && !!facilityId;

  const handleDrop = useCallback((accepted: File[]) => {
    setFile(accepted[0] ?? null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file || !facilityId) return;
    setIsUploading(true);
    try {
      const launched = await submit({
        file,
        facilityId,
        onDiscardRequested: discard.requestConfirm,
      });
      if (launched) setUploadDialogOpen(false);
    } catch (err) {
      alerts.error("Failed to upload bill of lading.", err instanceof Error ? err : undefined);
    } finally {
      setIsUploading(false);
    }
  }, [file, facilityId, submit, discard.requestConfirm, alerts]);

  const handleClose = useCallback(() => {
    if (!isUploading) {
      setFile(null);
      setDropRejectedFiles([]);
      onClose();
    }
  }, [isUploading, onClose]);

  return {
    facility,
    setFacility,
    file,
    setFile,
    dropRejectedFiles,
    setDropRejectedFiles,
    isUploading,
    uploadDialogOpen,
    facilityId,
    canSubmit,
    handleDrop,
    handleSubmit,
    handleClose,
    discardConfirmOpen: discard.confirmOpen,
    setDiscardConfirmOpen: (open: boolean) =>
      open ? discard.requestConfirm() : discard.cancelConfirm(),
    isDiscarding: discard.discarding,
    handleDiscard: discard.discard,
  };
}
